// src/pages/Users/Inscription/components/GroupDetailsDrawer.jsx

import React, { useEffect } from "react";
import {
  FaCheck,
  FaClock,
  FaEuroSign,
  FaLock,
  FaMapMarkerAlt,
  FaTimes,
  FaUserFriends,
} from "react-icons/fa";

import {
  formatEuro,
  getColorMeta,
  getGroupQuota,
  getGroupSchedules,
  getGroupTeachers,
  normalizeGroupYears,
} from "../utils/inscriptionUtils";

export default function GroupDetailsDrawer({
  group,
  dojo,
  disabled,
  unavailableReason,
  onClose,
  onSelect,
}) {
  useEffect(() => {
    if (!group) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [group, onClose]);

  if (!group) return null;

  const color = getColorMeta(group.color_groupe);
  const quota = getGroupQuota(group);
  const schedules = getGroupSchedules(group);
  const teachers = getGroupTeachers(group);
  const years = normalizeGroupYears(group.annees_naissance);

  const licence = Number(group.tarif_licence || 0);
  const cotisation = Number(group.tarif_cotisation || 0);
  const total = licence + cotisation;

  return (
    <aside
      className="
        fixed bottom-0 left-0 right-0 z-[9999]
        max-h-[88dvh] overflow-hidden rounded-t-[2rem]
        border border-slate-200 bg-white shadow-2xl
        dark:border-slate-800 dark:bg-slate-900
        md:left-auto md:right-4 md:top-4 md:bottom-4
        md:h-auto md:max-h-[calc(100dvh-2rem)] md:w-full md:max-w-xl
        md:rounded-[2rem]
      "
    >
      <div className={`h-2 ${color.solid}`} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {disabled ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                <FaLock />
                {unavailableReason || "Inscription fermée"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <FaCheck />
                Inscription ouverte
              </span>
            )}

            {group.discipline_groupe ? (
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${color.light} ${color.text}`}
              >
                {group.discipline_groupe}
              </span>
            ) : null}
          </div>

          <h2 className="mt-3 text-2xl font-bold leading-tight text-slate-950 dark:text-white">
            {group.nom || "Groupe"}
          </h2>

          <p className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
            <FaMapMarkerAlt />
            {dojo?.dojo_nom || "Lieu non renseigné"}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[calc(88dvh-150px)] overflow-y-auto p-5 md:max-h-[calc(100dvh-12rem)]">
        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Années de naissance
            </h3>

            {years.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {years.map((year) => (
                  <span
                    key={year}
                    className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                  >
                    {year}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                Non renseigné.
              </p>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              <FaClock />
              Horaires
            </h3>

            {schedules.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {schedules.map((schedule) => (
                  <span
                    key={schedule}
                    className="rounded-full bg-white px-3 py-1.5 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                  >
                    {schedule}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                Aucun horaire renseigné.
              </p>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              <FaUserFriends />
              Enseignant(s)
            </h3>

            {teachers.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {teachers.map((teacher) => (
                  <span
                    key={teacher}
                    className="rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                  >
                    {teacher}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                Aucun enseignant renseigné.
              </p>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              <FaEuroSign />
              Tarifs
            </h3>

            <div className="grid gap-3">
              <div className="flex justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900">
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Licence
                </span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {formatEuro(licence)}
                </span>
              </div>

              <div className="flex justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900">
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Cotisation
                </span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {formatEuro(cotisation)}
                </span>
              </div>

              <div className="flex justify-between rounded-2xl bg-emerald-50 px-4 py-3 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <span className="text-sm font-bold">Total</span>
                <span className="font-bold">{formatEuro(total)}</span>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Quota
            </h3>

            {quota.hasQuota ? (
              <>
                <div className="mt-3 flex justify-between text-sm font-bold text-slate-600 dark:text-slate-300">
                  <span>
                    {quota.members} / {quota.quota} inscrit(s)
                  </span>
                  <span>{quota.percent}%</span>
                </div>

                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className={`h-full rounded-full ${
                      quota.isFull ? "bg-rose-500" : color.solid
                    }`}
                    style={{ width: `${quota.percent}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                Aucun quota défini.
              </p>
            )}
          </section>

          {group.info_groupe ? (
            <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                Informations du groupe
              </h3>

              <div
                className="prose prose-sm mt-3 max-w-none text-slate-700 dark:prose-invert dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: group.info_groupe }}
              />
            </section>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold transition ${
              disabled
                ? "cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                : "bg-[#013E8E] text-white hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
            }`}
          >
            {disabled ? (
              <>
                <FaLock />
                Inscription impossible
              </>
            ) : (
              <>
                <FaCheck />
                Choisir ce groupe
              </>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}